import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Checkbox } from '../../components/ui/checkbox';

import { Input } from '../../components/ui/input';
import { Globe, ChevronDown, X, Info } from 'lucide-react';
import { TERRITOIRES_OPTIONS, TERRITOIRES_PAR_ZONE } from './constants';

/**
 * Sélecteur multi-territoires avec ventilation du CA par territoire.
 *
 * Props:
 * - territoires : string[] (ids sélectionnés)
 * - territoiresCA : Record<id, number> (CA par territoire, en €)
 * - chiffreAffairesGlobal : number — CA total verrouillé (readonly)
 * - onChange({ territoires, territoires_ca })
 *
 * Si un seul territoire sélectionné → pas de tableau de ventilation
 * (le CA global suffit). Si plusieurs → tableau ventilé obligatoire.
 */
export default function MultiTerritoireSelector({
  territoires = [],
  territoiresCA = {},
  chiffreAffairesGlobal = 0,
  onChange,
  editable = false,
}) {
  const [open, setOpen] = useState(false);

  // Helpers
  const selectedSet = useMemo(() => new Set(territoires), [territoires]);
  const selectedTerritoires = useMemo(
    () => TERRITOIRES_OPTIONS.filter((t) => selectedSet.has(t.id)),
    [selectedSet]
  );

  const totalVentile = useMemo(
    () => Object.values(territoiresCA).reduce((acc, v) => acc + (Number(v) || 0), 0),
    [territoiresCA]
  );

  const reste = Math.max(0, Number(chiffreAffairesGlobal) - totalVentile);

  const toggle = useCallback(
    (id) => {
      const next = new Set(selectedSet);
      let nextCA = { ...territoiresCA };
      if (next.has(id)) {
        next.delete(id);
        delete nextCA[id];
      } else {
        next.add(id);
        // Auto-affecte le "reste" sur le nouveau territoire si rien encore saisi
        if (!nextCA[id] && reste > 0 && next.size === 1) {
          nextCA[id] = Number(chiffreAffairesGlobal) || 0;
        } else if (!nextCA[id] && reste > 0) {
          nextCA[id] = reste;
        }
      }
      onChange({ territoires: Array.from(next), territoires_ca: nextCA });
    },
    [selectedSet, territoiresCA, reste, chiffreAffairesGlobal, onChange]
  );

  const updateCA = useCallback(
    (id, value) => {
      const n = parseFloat(String(value).replace(/[^0-9.,-]/g, '').replace(',', '.'));
      const nextCA = { ...territoiresCA };
      if (Number.isFinite(n) && n > 0) nextCA[id] = n;
      else delete nextCA[id];
      onChange({ territoires, territoires_ca: nextCA });
    },
    [territoires, territoiresCA, onChange]
  );

  // Si 1 seul territoire sélectionné et son CA est vide, on auto-pré-remplit
  // avec le CA global pour éviter une UX bizarre.
  useEffect(() => {
    if (territoires.length === 1) {
      const id = territoires[0];
      const ca = territoiresCA[id];
      if ((ca === undefined || ca === null || ca === 0) && Number(chiffreAffairesGlobal) > 0) {
        onChange({
          territoires,
          territoires_ca: { ...territoiresCA, [id]: Number(chiffreAffairesGlobal) },
        });
      }
    }
  }, [territoires.length, chiffreAffairesGlobal]); // eslint-disable-line

  const summaryLabel =
    selectedTerritoires.length === 0
      ? 'Selectionner un ou plusieurs territoires...'
      : `${selectedTerritoires.length} territoire${selectedTerritoires.length > 1 ? 's' : ''} selectionne${selectedTerritoires.length > 1 ? 's' : ''}`;

  return (
    <div className="space-y-3" data-testid="multi-territoire-selector">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 rounded-md border-2 px-3 h-12 hover:border-blue-400 transition-colors bg-white text-left"
            data-testid="multi-territoire-trigger"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className={`truncate text-sm ${selectedTerritoires.length === 0 ? 'text-slate-400' : 'text-slate-700 font-medium'}`}>
                {summaryLabel}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[480px] max-h-[420px] overflow-y-auto p-0" align="start">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0">
            <div className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
              Cochez les territoires d&apos;intervention
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
              <Info className="h-3 w-3" />
              Selection multiple — le CA pourra etre ventile ci-dessous.
            </div>
          </div>
          {Object.entries(TERRITOIRES_PAR_ZONE).map(([zone, territoiresList]) => (
            <div key={zone} className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/60">
                {zone}
              </div>
              {territoiresList.map((t) => {
                const checked = selectedSet.has(t.id);
                return (
                  <label
                    key={t.id}
                    className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors ${
                      checked ? 'bg-blue-50/60 hover:bg-blue-100/60' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`multi-territoire-option-${t.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(t.id)}
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${checked ? 'text-blue-900' : 'text-slate-800'}`}>
                        {t.nom}
                      </div>
                      <div className="text-[10px] text-slate-400">{t.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          ))}
        </PopoverContent>
      </Popover>

      {/* Ventilation CA si >= 1 territoire (utile pour multi, masque non bloquante pour 1) */}
      {selectedTerritoires.length >= 1 && (
        <div className="rounded-md border border-blue-200 bg-blue-50/30 p-3 space-y-2" data-testid="multi-territoire-ventilation">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-blue-900 uppercase tracking-wider">
              {(editable || selectedTerritoires.length > 1)
                ? `Ventilation du CA par territoire (${selectedTerritoires.length})`
                : 'Territoire selectionne'}
            </div>
            {!editable && selectedTerritoires.length > 1 && (
              <div className="text-[10px] text-slate-600">
                Ventile : <strong>{totalVentile.toLocaleString('fr-FR')} €</strong> / {Number(chiffreAffairesGlobal).toLocaleString('fr-FR')} €
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            {selectedTerritoires.map((t) => (
              <div key={t.id} className="flex items-center gap-2" data-testid={`multi-territoire-row-${t.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(t.id)}
                  className="text-slate-400 hover:text-red-500 flex-shrink-0"
                  title="Retirer ce territoire"
                  data-testid={`multi-territoire-remove-${t.id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className="flex-1 text-sm font-medium text-slate-800 truncate">{t.nom}</div>
                {(editable || selectedTerritoires.length > 1) ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={territoiresCA[t.id] ?? ''}
                      onChange={(e) => updateCA(t.id, e.target.value)}
                      placeholder="0"
                      className="h-8 w-32 text-right font-mono text-sm border-blue-200 focus:border-blue-500"
                      data-testid={`multi-territoire-ca-input-${t.id}`}
                    />
                    <span className="text-xs text-slate-500">€ HT</span>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    CA global : {Number(chiffreAffairesGlobal).toLocaleString('fr-FR')} € HT
                  </div>
                )}
              </div>
            ))}
          </div>
          {!editable && selectedTerritoires.length > 1 && Number(chiffreAffairesGlobal) > 0 && totalVentile !== Number(chiffreAffairesGlobal) && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
              {totalVentile < Number(chiffreAffairesGlobal)
                ? <>Il reste <strong>{reste.toLocaleString('fr-FR')} €</strong> a ventiler.</>
                : <>La ventilation depasse le CA global de <strong>{(totalVentile - Number(chiffreAffairesGlobal)).toLocaleString('fr-FR')} €</strong>.</>
              }
            </div>
          )}
        </div>
      )}

      {selectedTerritoires.length === 0 && (
        <div className="text-[11px] text-slate-500 italic">
          Aucun territoire selectionne. Le territoire par defaut sera <em>France Metropolitaine</em>.
        </div>
      )}
    </div>
  );
}
